import React from 'react';
import {
  getBudgetPerPersonString, getFormattedDateLongMonthDDYYYY, getTotalBudgetString,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import {
  Box, Typography, Stack, Skeleton,
} from '@mui/material';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { BookingDetailsProps, TripType } from './types';
import {
  formatArrayToString, getCountryNames, joinDestinationNames,
} from '../../utils/helpers';
import InfoTooltip from './info-tooltip';

const BookingDetails = ({
  tripType,
  tripBudget,
  countries,
  departureDate,
  description,
  destinations,
  isLoading,
  numAdults,
  numChildren,
  partnerName,
  returnDate,
  salesRecordings,
  hideDetails = false,
}: BookingDetailsProps) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const destinationNames = Array.isArray(destinations)
    ? joinDestinationNames(destinations, ', ')
    : undefined;
  const countryNames = countries ? formatArrayToString(getCountryNames(countries, countryCodes)) : undefined;

  // If the trip is sold use the total of all sales recordings, if not use the trip match budget per person
  const totalTripPrice = salesRecordings && salesRecordings.reduce((accumulator, currentRecording) => accumulator + currentRecording.price.amount, 0);
  const totalPriceString = totalTripPrice && `Total Trip Price: ${getTotalBudgetString(totalTripPrice, 2, 2, salesRecordings[0].price.currencyCode)}`;

  const budgetPerPersonString = `Budget Per Person: ${getBudgetPerPersonString(tripBudget, numAdults, numChildren)}`;
  const totalBudgetString = `Total Budget: ${getTotalBudgetString(tripBudget)}`;
  const numOfTravelersString = `${numAdults} Adult${numAdults !== 1 ? 's' : ''}${numChildren ? `, ${numChildren} Child${numChildren !== 1 ? 'ren' : ''}` : ''}`;

  const conversionPromptString = salesRecordings && salesRecordings.length ? `If this trip was paid in a different currency, please note that this is an approximate conversion
  to ${salesRecordings[0].price.currencyCode}. No additional payment is required.` : '';
  return (
    <Box>
      {isLoading ? (
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">
            <Skeleton width={200} />
          </Typography>
          <Typography variant="body1"><Skeleton width={100} /></Typography>
          <Stack spacing={0.5}>
            <Typography variant="body1">
              <Skeleton width={250} />
            </Typography>
            <Typography variant="body1">
              <Skeleton width={200} />
            </Typography>
            <Typography variant="body1">
              <Skeleton width={150} />
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={0.5}>
          <Typography variant="bodyBold" sx={{ mb: '8px !important' }}>
            {tripType === TripType.TripMatch ? `Booked with ${partnerName}` : 'Original Request'}
          </Typography>
          {(countryNames || destinationNames) && (
            <Typography variant="body1" color="text.secondary">{countryNames || destinationNames}</Typography>
          )}
          {!hideDetails && (
            <Stack spacing={0.5}>
              {departureDate && returnDate && (
                <Typography variant="body1" color="text.secondary">
                  {`${getFormattedDateLongMonthDDYYYY(departureDate)} - ${getFormattedDateLongMonthDDYYYY(returnDate)}`}
                </Typography>
              )}
              {salesRecordings && salesRecordings.length > 0 ? (
                <Stack direction="row" spacing={0.5}>
                  <Typography variant="body1" color="text.secondary">
                    {totalPriceString}
                  </Typography>
                  <InfoTooltip tooltipText={conversionPromptString} />
                </Stack>
              ) : (
                <>
                  <Typography variant="body1" color="text.secondary">{budgetPerPersonString}</Typography>
                  <Typography variant="body1" color="text.secondary">{totalBudgetString}</Typography>
                </>
              )}
              <Typography variant="body1" color="text.secondary">
                {numOfTravelersString}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {description && (
        <>
          <Typography variant="bodyBold" color="text.primary" mt={4}>Description</Typography>
          <Typography color="text.secondary" variant="body1" mt={1}>{description}</Typography>
        </>
      )}
    </Box>
  );
};
export default BookingDetails;
